/**
 * Reading the record, as its owner.
 *
 * READ ONLY, deliberately. This portal has no write path: no create, no
 * update, no status change. §14.3 describes one-tap revocation of a consent,
 * and that is a real feature — but it is a write from a patient identity that
 * nothing in this platform has ever exercised, and it needs its own consent
 * ledger work and its own audit story. Adding a `fetch` with `method: 'POST'`
 * here later should feel like a decision, not a convenience.
 *
 * PURPOSE-OF-USE IS `PATRQT`, NOT `TREAT`.
 *
 * §13: "purpose limitation: purpose-of-use accompanies every access and is
 * enforced, not merely recorded. Treatment ≠ research ≠ operations ≠
 * marketing." A patient reading their own record is not treating anyone.
 * `TREAT` — which the clinician app sends, correctly, for itself — would be a
 * false statement about why the data is being read, written into the
 * `AuditEvent` that §12.4 promises the patient can inspect. The one place a
 * patient can check who looked at their record should not lie about it.
 *
 * `PATRQT` (patient requested) is accepted by the gateway today. Note that the
 * gateway does not currently CONSTRAIN a patient's own reads by purpose — it
 * accepts any recognised value, and no value at all, where a service caller is
 * correctly refused. Sending the truthful one anyway is the difference between
 * a record that happens to be right and one that is right on purpose.
 *
 * Every response arrives having passed the Consent Decision Point and produced
 * an AuditEvent (§1.5, §1.6, §14.2). Nothing here decides visibility; §1.6 is
 * explicit that hiding in the UI is not access control.
 */

import type { FhirBundle } from '@glycemia/web-kit';

import { currentAccessToken, signOut } from './auth.js';

const FHIR_BASE = import.meta.env['VITE_FHIR_BASE'] ?? '/fhir/R4';
const AUDIT_BASE = import.meta.env['VITE_AUDIT_BASE'] ?? '/audit';
const CONSENT_BASE = import.meta.env['VITE_CONSENT_BASE'] ?? '/consent';

/** Why the portal reads. See the note above. */
const PURPOSE_OF_USE = 'PATRQT';

export class HttpError extends Error {
  public constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class SessionExpiredError extends Error {
  public constructor() {
    super('Your session has ended. Please sign in again.');
    this.name = 'SessionExpiredError';
  }
}

/**
 * A refusal is NOT an empty result.
 *
 * This distinction is the reason this class exists rather than a plain
 * HttpError. §14.2 denies a read the Consent Decision Point does not permit,
 * and a screen that renders that as "you have no medications" tells the patient
 * something false about their own care. Diagnosing this platform has already
 * produced two wrong conclusions from exactly that collapse, in throwaway
 * scripts. On a patient's screen it would not be throwaway.
 */
export class AccessRefusedError extends Error {
  public constructor(public readonly reason: string) {
    super(reason);
    this.name = 'AccessRefusedError';
  }
}

interface OperationOutcome {
  readonly issue?: { readonly diagnostics?: string; readonly code?: string }[];
}

const request = async <T>(url: string): Promise<T> => {
  const token = await currentAccessToken();
  if (token === null) throw new SessionExpiredError();

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/fhir+json',
      'X-Glycemia-Purpose': PURPOSE_OF_USE,
    },
  });

  if (response.status === 401) {
    signOut();
    throw new SessionExpiredError();
  }

  if (!response.ok) {
    let diagnostics = `The server returned HTTP ${response.status}.`;
    try {
      const outcome = (await response.json()) as OperationOutcome;
      const detail = outcome.issue?.[0]?.diagnostics;
      if (detail !== undefined && detail !== '') diagnostics = detail;
    } catch {
      // A non-JSON body means a proxy answered, not the application. The
      // status is all there is to report.
    }
    // 403 is its own type. The server's own reason travels with it, because
    // the patient is entitled to know that something was withheld and why —
    // §14.3's transparency is not only about the consent dashboard.
    if (response.status === 403) throw new AccessRefusedError(diagnostics);
    throw new HttpError(response.status, diagnostics);
  }

  return (await response.json()) as T;
};

/** Entries of a bundle, or an empty list. Never conflated with a refusal. */
const entriesOf = <T>(bundle: FhirBundle<T> | null): readonly T[] =>
  (bundle?.entry ?? []).flatMap((entry) => (entry.resource === undefined ? [] : [entry.resource]));

export const readOwnRecord = <T>(reference: string): Promise<T> =>
  request<T>(`${FHIR_BASE}/${reference}`);

export const searchForPatient = async <T>(
  resourceType: string,
  patientId: string,
  extra = '',
): Promise<readonly T[]> =>
  entriesOf(
    await request<FhirBundle<T>>(
      `${FHIR_BASE}/${resourceType}?patient=${encodeURIComponent(patientId)}&_count=200${extra}`,
    ),
  );

/**
 * Who has looked at this record (§12.4).
 *
 * "Audit records are queryable by the privacy officer and, per policy, by the
 * PATIENT — an access report is a product feature, not an afterthought."
 * This is that feature. It reads the audit ledger, not the FHIR server.
 */
export const readAccessReport = async (patientId: string): Promise<readonly unknown[]> =>
  entriesOf(
    await request<FhirBundle<unknown>>(
      `${AUDIT_BASE}/Patient/${encodeURIComponent(patientId)}/$access-report?limit=100`,
    ),
  );

/** One consent grant, as the consent engine returns it. */
export interface ConsentGrant {
  readonly id: string;
  readonly status: string;
  readonly actorRef: string | null;
  readonly actorType: string | null;
  readonly permittedPurposes: readonly string[];
  readonly permittedActions: readonly string[];
  readonly permittedResourceTypes: readonly string[] | null;
  readonly withheldSensitivities: readonly string[];
  readonly recipientRef: string | null;
  readonly startEpochMs: number | null;
  readonly endEpochMs: number | null;
}

/**
 * What the patient has agreed to, and who it covers (§14.3).
 *
 * "A permanent 'who can see what' dashboard." Displayed here; changing it is a
 * write and is out of this slice.
 *
 * NOT A FHIR BUNDLE. `$consents` answers `{ total, grants: [...] }`, so the
 * bundle reader used for everything else returns an empty list against it —
 * silently, and indistinguishably from "you have consented to nothing". That
 * is the failure this whole portal is written to avoid, caught here by reading
 * the endpoint rather than assuming its shape.
 */
export const readConsents = async (patientId: string): Promise<readonly ConsentGrant[]> => {
  const body = await request<{ total?: number; grants?: readonly ConsentGrant[] }>(
    `${CONSENT_BASE}/Patient/${encodeURIComponent(patientId)}/$consents`,
  );
  return body.grants ?? [];
};
